import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, skipWhile, take } from 'rxjs';
import { PROCESS_SERVICE_STATES } from '../../../_constants/values/values.service';
import { ApiProxyUsersService } from '../../requests/api-proxy-users/api-proxy-users.component';
import { UsersService } from '../users/users.component';

@Injectable({
    providedIn: 'root'
})
export class ProxyUsersService {
    readonly onListUsersClients$: BehaviorSubject<string> = new BehaviorSubject<string>(PROCESS_SERVICE_STATES.waiting);
    private markToUpdateUsersClients = true;
    private usersClients: Record<string, number[]> = {};
    private proxyUserInfo: any = null;

    constructor(
        private readonly apiProxyUsersService: ApiProxyUsersService,
        private readonly usersService: UsersService
    ) { }

    public listUsersClients(): Observable<Record<string, number[]>> {
        if (!this.markToUpdateUsersClients) {
            return of(this.usersClients);
        }

        if (this.onListUsersClients$.value === PROCESS_SERVICE_STATES.inProgress) {
            return this.onListUsersClients$.asObservable().pipe(
                skipWhile((res: any) => res !== PROCESS_SERVICE_STATES.done),
                take(1),
                map(() => this.usersClients)
            );
        }

        this.onListUsersClients$.next(PROCESS_SERVICE_STATES.inProgress);
        return this.apiProxyUsersService.listClients().pipe(
            map((resp: Record<string, number[]>) => {
                this.usersClients = resp ?? {};
                this.markToUpdateUsersClients = false;
                this.onListUsersClients$.next(PROCESS_SERVICE_STATES.done);
                return this.usersClients;
            }),
            catchError((error: any) => {
                this.markToUpdateUsersClients = true;
                this.onListUsersClients$.next(PROCESS_SERVICE_STATES.done);
                throw error;
            })
        );
    }

    public setUsersClients(userId: number, clients: number[]): Observable<any> {
        return this.apiProxyUsersService.setClients(userId, clients).pipe(
            map((resp: any) => {
                this.usersClients[userId] = clients;
                return resp;
            }),
            catchError((error: any) => {
                throw error;
            })
        );
    }

    public listInfo(): Observable<any> {
        if (this.proxyUserInfo) {
            return of(this.proxyUserInfo);
        }

        return this.apiProxyUsersService.getInfo().pipe(
            map((resp: any) => {
                if (resp) {
                    this.proxyUserInfo = resp;
                }
                return resp;
            }),
            catchError((error: any) => {
                throw error;
            })
        );
    }

    public getUsersClients(userId: number): number[] {
        return this.usersClients[userId] ?? [];
    }

    public hasUserClients(userId: number): boolean {
        return Object.prototype.hasOwnProperty.call(this.usersClients, userId);
    }

    public getUsersClientsInfo(): any {
        return this.proxyUserInfo;
    }

    public markToUpdateUsersClientsList(): void {
        this.markToUpdateUsersClients = true;
    }
}
